/**
 * Job executions container: inline view with PropertyFilter, in-progress/completed
 * segmented control, stage progress, job status, CR links, alpha deployment status.
 *
 * Extracted from UpgradeMedicPage.tsx — preserves all existing behavior.
 */
import React, { useState } from 'react';
import {
  Box,
  Button,
  Container,
  Header,
  Pagination,
  PropertyFilter,
  SegmentedControl,
  SpaceBetween,
  StatusIndicator,
  Table
} from '@amzn/awsui-components-core';
import { PropertyFilterProps } from '@amzn/awsui-components-core/property-filter';
import { AgentLogEntry, JobRun } from '../types';
import { ParsedLogEntry } from 'src/services/upgrade-medic/CloudWatchLogsService';
import { CloudWatchLogsService } from 'src/services/upgrade-medic/CloudWatchLogsService';
import { paginationAriaLabels } from 'src/i18n-strings';
import { ResourceTypeConfig } from '../config';
import { renderJobStatus, renderStageProgress, formatDuration, formatStartedAt, extractJobErrorMessage } from '../utils/jobDisplayUtils';

// PropertyFilter filtering properties
const JOB_FILTERING_PROPERTIES: PropertyFilterProps.FilteringProperty[] = [
  { key: 'jobId', propertyLabel: 'Job ID', groupValuesLabel: 'Job IDs', operators: [':', '!:', '=', '!='] },
  { key: 'status', propertyLabel: 'Status', groupValuesLabel: 'Statuses', operators: ['=', '!='] },
  { key: 'triggeredBy', propertyLabel: 'User', groupValuesLabel: 'Users', operators: [':', '!:', '=', '!='] },
  { key: 'pipeline', propertyLabel: 'Pipeline', groupValuesLabel: 'Pipelines', operators: [':', '!:', '=', '!='] },
  { key: 'stage', propertyLabel: 'Stage', groupValuesLabel: 'Stages', operators: ['=', '!='] }
];

interface JobExecutionsContainerProps {
  currentConfig: ResourceTypeConfig;
  userAlias: string;
  // Job data
  resourceInProgressJobs: JobRun[];
  resourceCompletedJobs: JobRun[];
  jobExecutionFilter: 'in-progress' | 'completed';
  setJobExecutionFilter: (filter: 'in-progress' | 'completed') => void;
  jobFilterQuery: PropertyFilterProps.Query;
  setJobFilterQuery: (query: PropertyFilterProps.Query) => void;
  filterJobsByQuery: (jobs: JobRun[], query: PropertyFilterProps.Query) => JobRun[];
  // Actions
  handleCancelJob: (job: JobRun) => void;
  handleRetryJob: (job: JobRun) => void;
  // Agent panel
  setSelectedJobForView: (job: JobRun | null) => void;
  setIsAgentPanelVisible: (visible: boolean) => void;
  setAgentLogs: React.Dispatch<React.SetStateAction<AgentLogEntry[]>>;
  setStreamingText: (text: string) => void;
  setProgressMessage: (msg: string) => void;
  setCloudWatchPollingJobId: (id: string | null) => void;
  currentJobIdRef: React.MutableRefObject<string | null>;
  agentLogs: AgentLogEntry[];
  // Historical tabs
  historicalJobTabs: Array<{ jobId: string; title: string }>;
  setHistoricalJobTabs: React.Dispatch<React.SetStateAction<Array<{ jobId: string; title: string }>>>;
  setHistoricalJobLogs: React.Dispatch<React.SetStateAction<Record<string, AgentLogEntry[]>>>;
  setActivePipelineTab: (tab: string) => void;
  subscribeToJob: (jobId: string) => Promise<'subscribed' | 'fallback'>;
}

const PROPERTY_FILTER_I18N: PropertyFilterProps.I18nStrings = {
  filteringAriaLabel: 'Filter job executions',
  filteringPlaceholder: 'Filter jobs',
  groupValuesText: 'Values',
  groupPropertiesText: 'Properties',
  operatorsText: 'Operators',
  operationAndText: 'and',
  operationOrText: 'or',
  operatorLessText: 'Less than',
  operatorLessOrEqualText: 'Less than or equal',
  operatorGreaterText: 'Greater than',
  operatorGreaterOrEqualText: 'Greater than or equal',
  operatorContainsText: 'Contains',
  operatorDoesNotContainText: 'Does not contain',
  operatorEqualsText: 'Equals',
  operatorDoesNotEqualText: 'Does not equal',
  editTokenHeader: 'Edit filter',
  propertyText: 'Property',
  operatorText: 'Operator',
  valueText: 'Value',
  cancelActionText: 'Cancel',
  applyActionText: 'Apply',
  allPropertiesLabel: 'All properties',
  tokenLimitShowMore: 'Show more',
  tokenLimitShowFewer: 'Show fewer',
  clearFiltersText: 'Clear filters',
  removeTokenButtonAriaLabel: (token) => `Remove filter: ${token.propertyKey || 'All'} ${token.operator} ${token.value}`
};

export const JobExecutionsContainer: React.FC<JobExecutionsContainerProps> = ({
  currentConfig,
  userAlias,
  resourceInProgressJobs,
  resourceCompletedJobs,
  jobExecutionFilter,
  setJobExecutionFilter,
  jobFilterQuery,
  setJobFilterQuery,
  filterJobsByQuery,
  handleCancelJob,
  handleRetryJob,
  setSelectedJobForView,
  setIsAgentPanelVisible,
  setAgentLogs,
  setStreamingText,
  setProgressMessage,
  setCloudWatchPollingJobId,
  currentJobIdRef,
  agentLogs,
  historicalJobTabs,
  setHistoricalJobTabs,
  setHistoricalJobLogs,
  setActivePipelineTab,
  subscribeToJob
}) => {
  const baseJobs = jobExecutionFilter === 'in-progress' ? resourceInProgressJobs : resourceCompletedJobs;
  const currentJobs = filterJobsByQuery(baseJobs, jobFilterQuery);

  const PAGE_SIZE = 20;
  const [currentPage, setCurrentPage] = useState(1);
  const totalPages = Math.max(1, Math.ceil(currentJobs.length / PAGE_SIZE));
  const paginatedJobs = currentJobs.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE);

  // Reset to page 1 when filter or tab changes
  React.useEffect(() => {
    setCurrentPage(1);
  }, [jobExecutionFilter, jobFilterQuery]);

  const loadCloudWatchLogs = async (jobId: string): Promise<AgentLogEntry[]> => {
    try {
      const logs = await CloudWatchLogsService.fetchAllJobLogs(jobId);
      return logs.map((l: ParsedLogEntry) => ({
        id: Date.now() + Math.random(),
        timestamp: l.timestamp,
        level: l.level,
        message: l.message,
        stage: l.stage
      }));
    } catch (error) {
      console.error('[Upgrade Medic] Failed to fetch CloudWatch logs:', error);
      return [];
    }
  };

  const handleJobClick = async (item: JobRun) => {
    setSelectedJobForView(item);
    setIsAgentPanelVisible(true);

    if (item.status === 'running' || item.status === 'connecting') {
      // ── In-progress job ──
      // Select first pipeline tab if available
      if (item.pipelines && item.pipelines.length > 0) {
        setActivePipelineTab(item.pipelines[0]);
      }

      if (currentJobIdRef.current === item.jobId && agentLogs.length > 0) {
        // Already streaming live from this job's WS — just show current logs
        setCloudWatchPollingJobId(null);
        setProgressMessage(`Viewing live logs for ${item.jobId}`);
        return;
      }

      // Always load CloudWatch logs first so the user sees something immediately
      setAgentLogs([]);
      setStreamingText('');
      setProgressMessage('Loading logs from CloudWatch...');

      const cwLogs = await loadCloudWatchLogs(item.jobId);
      if (cwLogs.length > 0) {
        setAgentLogs(cwLogs);
        setProgressMessage(`Loaded ${cwLogs.length} log entries from CloudWatch (auto-refreshing every 10s)`);
      } else {
        setProgressMessage('No logs available yet - job may be initializing (auto-refreshing every 10s)');
      }

      // Start CloudWatch polling for continuous updates
      setCloudWatchPollingJobId(item.jobId);

      // Attempt WS reconnect in the background for real-time streaming.
      // If it succeeds and starts receiving messages, the live logs will
      // supplement the CloudWatch polling. If it fails, polling continues.
      subscribeToJob(item.jobId).then((result) => {
        if (result === 'subscribed') {
          console.log('[Upgrade Medic] WS reconnect succeeded — live stream active alongside CloudWatch polling');
        } else {
          console.log('[Upgrade Medic] WS reconnect failed — continuing with CloudWatch polling');
        }
      });
    } else {
      // ── Completed/failed/cancelled job ──
      const historyTabId = `history-${item.jobId}`;
      const tabExists = historicalJobTabs.some((t) => t.jobId === item.jobId);
      if (!tabExists) {
        setHistoricalJobTabs((prev) => [...prev, { jobId: item.jobId, title: item.jobId.substring(0, 8) + '...' }]);
      }
      setActivePipelineTab(historyTabId);
      setProgressMessage('Loading logs from CloudWatch...');

      const cwLogs = await loadCloudWatchLogs(item.jobId);
      setHistoricalJobLogs((prev) => ({ ...prev, [item.jobId]: cwLogs }));
      setProgressMessage(cwLogs.length > 0 ? `Loaded ${cwLogs.length} log entries` : 'Failed to load logs from CloudWatch');
    }
  };

  return (
    <Container
      header={
        <Header
          variant="h2"
          description={`${currentConfig.name} version update job executions`}
          actions={
            <SegmentedControl
              selectedId={jobExecutionFilter}
              onChange={({ detail }) => setJobExecutionFilter(detail.selectedId as 'in-progress' | 'completed')}
              options={[
                { id: 'in-progress', text: `In Progress (${resourceInProgressJobs.length})` },
                { id: 'completed', text: `Completed (${resourceCompletedJobs.length})` }
              ]}
            />
          }
        >
          {currentConfig.name} Job Executions
        </Header>
      }
    >
      <Box padding={{ bottom: 'm' }}>
        <PropertyFilter
          query={jobFilterQuery}
          onChange={({ detail }) => setJobFilterQuery(detail)}
          filteringProperties={JOB_FILTERING_PROPERTIES}
          filteringPlaceholder="Filter jobs by job ID, status, user, pipeline, or stage..."
          filteringAriaLabel="Filter job executions"
          filteringEmpty="No suggestions"
          filteringLoadingText="Loading suggestions"
          i18nStrings={PROPERTY_FILTER_I18N}
          expandToViewport
        />
      </Box>
      {currentJobs.length === 0 ? (
        <Box textAlign="center" padding="xxl" color="text-body-secondary">
          {jobExecutionFilter === 'in-progress'
            ? `No ${currentConfig.name} jobs currently running. Select resources and start a version update.`
            : `No completed ${currentConfig.name} jobs yet. Job history will appear here after executions complete.`}
        </Box>
      ) : (
        <Table
          items={paginatedJobs}
          columnDefinitions={buildColumnDefinitions(jobExecutionFilter, userAlias, handleJobClick, handleCancelJob, handleRetryJob)}
          resizableColumns
          stickyHeader
          stickyColumns={{ first: 1, last: 0 }}
          variant="embedded"
          stripedRows
          wrapLines={false}
          pagination={
            <Pagination
              currentPageIndex={currentPage}
              pagesCount={totalPages}
              onChange={({ detail }) => setCurrentPage(detail.currentPageIndex)}
              ariaLabels={paginationAriaLabels(totalPages)}
            />
          }
          empty={
            <Box textAlign="center" padding="l">
              No jobs to display
            </Box>
          }
        />
      )}
    </Container>
  );
};

/** Build column definitions based on in-progress vs completed view. */
function buildColumnDefinitions(
  filter: 'in-progress' | 'completed',
  userAlias: string,
  onJobClick: (job: JobRun) => void,
  onCancel: (job: JobRun) => void,
  onRetry: (job: JobRun) => void
) {
  const columns: any[] = [
    {
      id: 'jobId',
      header: 'Job ID',
      cell: (item: JobRun) => (
        <Button variant="link" onClick={() => onJobClick(item)}>
          {item.jobId}
        </Button>
      ),
      width: 220,
      minWidth: 180
    }
  ];

  if (filter === 'in-progress') {
    columns.push({
      id: 'stage',
      header: 'Stage',
      cell: (item: JobRun) => (
        <SpaceBetween direction="horizontal" size="xs" alignItems="center">
          {renderStageProgress(item)}
          {(item.status === 'running' || item.status === 'connecting') && item.triggeredBy === userAlias && (
            <Button variant="icon" iconName="close" onClick={() => onCancel(item)} ariaLabel="Cancel job" />
          )}
        </SpaceBetween>
      ),
      width: 180,
      minWidth: 150
    });
  }

  if (filter === 'completed') {
    columns.push({
      id: 'status',
      header: 'Status',
      cell: (item: JobRun) => {
        if (item.status === 'failed') {
          const errorMsg = extractJobErrorMessage(item);
          const isDisconnectError =
            errorMsg && (errorMsg.toLowerCase().includes('server disconnected') || errorMsg.toLowerCase().includes('disconnected unexpectedly'));
          return (
            <SpaceBetween direction="vertical" size="xxxs">
              <SpaceBetween direction="horizontal" size="xs" alignItems="center">
                {renderJobStatus(item.status)}
                <Button variant="icon" iconName="refresh" onClick={() => onRetry(item)} ariaLabel="Retry failed job" />
              </SpaceBetween>
              {isDisconnectError && (
                <Box fontSize="body-s" color="text-status-error">
                  {errorMsg}
                </Box>
              )}
            </SpaceBetween>
          );
        }
        return renderJobStatus(item.status);
      },
      width: 200,
      minWidth: 160
    });
  }

  columns.push(
    {
      id: 'triggeredBy',
      header: 'User',
      cell: (item: JobRun) => <Box fontWeight="bold">{item.triggeredBy}</Box>,
      width: 110,
      minWidth: 90
    },
    {
      id: 'pipelines',
      header: 'Pipeline',
      cell: (item: JobRun) => renderPipelineCell(item),
      width: 250,
      minWidth: 180
    },
    {
      id: 'functions',
      header: 'Funcs',
      cell: (item: JobRun) => {
        if (item.totalResources !== undefined && item.totalResources !== null) return `${item.totalResources}`;
        return item.functions?.length ? `${item.functions.length}` : '0';
      },
      width: 80,
      minWidth: 60
    },
    {
      id: 'startedAt',
      header: 'Started',
      cell: (item: JobRun) => formatStartedAt(item.startedAt),
      width: 130,
      minWidth: 100
    },
    {
      id: 'duration',
      header: 'Duration',
      cell: (item: JobRun) => {
        // Check multiple possible stage names for CR creation completion
        const stages = item.stages || {};
        const crStage = stages.CR_CREATION || stages.cr_creation;
        const buildStage = stages.BUILD || stages.build;
        const deployStage = stages.DEPLOYMENT || stages.deployment;
        const alphaStage = stages.ALPHA_DEPLOYMENT || stages.alpha_deployment;
        const completedStage = stages.COMPLETED || stages.completed;

        // If CR_CREATION or BUILD has completedAt, use it
        const endStage = crStage || buildStage || deployStage;
        if (endStage?.completedAt) {
          return formatDuration(item.startedAt, new Date(endStage.completedAt));
        }
        // If a later stage (ALPHA_DEPLOYMENT, COMPLETED) exists, CR must have completed — use job completedAt or startedAt of later stage
        if (alphaStage?.startedAt) {
          return formatDuration(item.startedAt, new Date(alphaStage.startedAt));
        }
        if (completedStage?.startedAt) {
          return formatDuration(item.startedAt, new Date(completedStage.startedAt));
        }
        // Job completed — use job completedAt
        if (item.completedAt) {
          return formatDuration(item.startedAt, item.completedAt);
        }
        // Still in progress — show live timer
        return formatDuration(item.startedAt);
      },
      width: 110,
      minWidth: 80
    },
    {
      id: 'cr',
      header: 'CR Link',
      cell: (item: JobRun) => renderCRCell(item),
      width: 120,
      minWidth: 100
    },
    {
      id: 'alphaDeployment',
      header: 'Alpha Deployment (Optional)',
      cell: (item: JobRun) => {
        const stages = item.stages || {};
        const alphaStage = stages.ALPHA_DEPLOYMENT || stages.alpha_deployment || stages.DEPLOYING || stages.deploying;
        const isJobTerminal =
          item.status === 'completed' ||
          item.status === 'completed_with_alpha_deployment_failure' ||
          item.status === 'failed' ||
          item.status === 'cancelled';

        // Also check job status for alpha deployment failure
        if (!alphaStage) {
          if (item.status === 'completed_with_alpha_deployment_failure') {
            return <StatusIndicator type="error">Failed</StatusIndicator>;
          }
          return <Box color="text-body-secondary">-</Box>;
        }
        let status = alphaStage.status;
        // Always use job-level status for alpha deployment — stage status may say 'completed' even when deployment failed
        if (item.status === 'completed_with_alpha_deployment_failure') {
          status = 'failed';
        } else if (isJobTerminal && status === 'running') {
          status = item.status === 'completed' ? 'completed' : 'failed';
        }
        const alphaDuration =
          alphaStage.startedAt && alphaStage.completedAt
            ? formatDuration(new Date(alphaStage.startedAt), new Date(alphaStage.completedAt))
            : alphaStage.startedAt && isJobTerminal && item.completedAt
            ? formatDuration(new Date(alphaStage.startedAt), new Date(item.completedAt))
            : alphaStage.startedAt
            ? formatDuration(new Date(alphaStage.startedAt))
            : null;
        const durationText = alphaDuration ? ` (${alphaDuration})` : '';
        if (status === 'completed') return <StatusIndicator type="success">{`Deployed${durationText}`}</StatusIndicator>;
        if (status === 'failed') return <StatusIndicator type="error">{`${alphaStage.message || 'Failed'}${durationText}`}</StatusIndicator>;
        if (status === 'running') return <StatusIndicator type="in-progress">{`Deploying${durationText}`}</StatusIndicator>;
        if (status === 'pending') return <StatusIndicator type="pending">Pending</StatusIndicator>;
        return <Box color="text-body-secondary">{status || '-'}</Box>;
      },
      width: 240,
      minWidth: 160
    }
  );

  return columns;
}

/** Render the pipeline cell with special handling for delete/manual/stack jobs. */
function renderPipelineCell(item: JobRun): React.ReactNode {
  const pipelineName =
    item.pipelineId ||
    (item.pipelines && item.pipelines.length === 1
      ? item.pipelines[0]
      : item.pipelines && item.pipelines.length > 1
      ? `${item.pipelines.length} pipelines`
      : '-');

  if (pipelineName === 'Delete Stack') {
    const deletedStacks = item.pipelines || item.artifacts?.stackNames || [];
    if (deletedStacks.length > 0) {
      const displayText = deletedStacks.slice(0, 2).join(', ') + (deletedStacks.length > 2 ? '...' : '');
      return (
        <span title={`Deleted stacks:\n${deletedStacks.join('\n')}`} style={{ fontStyle: 'italic' }}>
          🗑️ {displayText}
        </span>
      );
    }
    return <span style={{ fontStyle: 'italic' }}>🗑️ Delete Stack</span>;
  }

  if (pipelineName === 'Manually created') {
    const funcNames = item.functions?.slice(0, 3).join(', ') || '-';
    const hasMore = (item.functions?.length || 0) > 3;
    const displayText = hasMore ? `${funcNames}...` : funcNames;
    return (
      <span title={`Manually created lambdas:\n${item.functions?.join('\n') || '-'}`} style={{ fontStyle: 'italic' }}>
        λ {displayText}
      </span>
    );
  }

  if (pipelineName === 'Manual Deployed Stacks') {
    const stackToLambdas = item.artifacts?.stackToLambdas as Record<string, string[]> | undefined;
    const stackNames = item.artifacts?.stackNames as string[] | undefined;

    if (stackToLambdas && Object.keys(stackToLambdas).length > 0) {
      const entries = Object.entries(stackToLambdas);
      const displayParts = entries.slice(0, 2).map(([stack, lambdas]) => {
        const lambdaDisplay = lambdas.slice(0, 2).join(', ') + (lambdas.length > 2 ? '...' : '');
        return `${stack}: ${lambdaDisplay}`;
      });
      const displayText = displayParts.join('; ') + (entries.length > 2 ? '...' : '');
      const fullList = entries.map(([stack, lambdas]) => `${stack}:\n  ${lambdas.join('\n  ')}`).join('\n\n');
      return (
        <span title={`Manual Deployed Stacks:\n${fullList}`} style={{ fontStyle: 'italic' }}>
          📦 {displayText}
        </span>
      );
    }

    if (stackNames && stackNames.length > 0) {
      const displayText = stackNames.slice(0, 2).join(', ') + (stackNames.length > 2 ? '...' : '');
      return (
        <span title={`Stacks:\n${stackNames.join('\n')}`} style={{ fontStyle: 'italic' }}>
          📦 {displayText}
        </span>
      );
    }

    const funcNames = item.functions?.slice(0, 2).join(', ') || '-';
    const hasMore = (item.functions?.length || 0) > 2;
    return (
      <span title={`Lambdas:\n${item.functions?.join('\n') || '-'}`} style={{ fontStyle: 'italic' }}>
        📦 {funcNames}
        {hasMore ? '...' : ''}
      </span>
    );
  }

  return <span title={item.pipelineId || item.pipelines?.join(', ') || '-'}>{pipelineName}</span>;
}

/** Render CR link(s) cell. */
function renderCRCell(item: JobRun): React.ReactNode {
  const crLinks = item.artifacts?.crLinks || (item.artifacts?.crLink ? [item.artifacts.crLink] : []);
  if (crLinks.length === 0) return '-';
  if (crLinks.length === 1) {
    return (
      <Button variant="link" href={crLinks[0]} target="_blank" iconName="external">
        CR
      </Button>
    );
  }
  return (
    <SpaceBetween direction="vertical" size="xxxs">
      {crLinks.map((crUrl: string, idx: number) => (
        <Button key={idx} variant="link" href={crUrl} target="_blank" iconName="external">
          CR {idx + 1}
        </Button>
      ))}
    </SpaceBetween>
  );
}
